"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import {
  pathMatches,
  type NavGroup,
  type NavItem,
  type NavModel,
} from "@/components/nav-config";

type Props = {
  model: NavModel;
  extras: React.ReactNode;
};

function linkClass(active: boolean) {
  return active
    ? "text-stone-950 dark:text-white"
    : "text-stone-700 hover:text-stone-950 dark:text-stone-300 dark:hover:text-white";
}

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <span className="relative block h-4 w-5" aria-hidden>
      <span
        className={`absolute left-0 top-0 h-0.5 w-5 rounded-full bg-current transition ${open ? "translate-y-[7px] rotate-45" : ""}`}
      />
      <span
        className={`absolute left-0 top-[7px] h-0.5 w-5 rounded-full bg-current transition ${open ? "opacity-0" : ""}`}
      />
      <span
        className={`absolute left-0 top-[14px] h-0.5 w-5 rounded-full bg-current transition ${open ? "-translate-y-[7px] -rotate-45" : ""}`}
      />
    </span>
  );
}

function DesktopDropdown({
  group,
  pathname,
}: {
  group: NavGroup;
  pathname: string;
}) {
  const t = useTranslations("nav");
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const groupActive = group.items.some((item) =>
    pathMatches(pathname, item.href),
  );

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
        className={`inline-flex items-center gap-1 ${groupActive ? "text-stone-950 dark:text-white" : "text-stone-700 hover:text-stone-950 dark:text-stone-300 dark:hover:text-white"}`}
      >
        {t(`groups.${group.key}`)}
        <span aria-hidden className="text-[0.65rem] opacity-70">
          ▾
        </span>
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute left-0 top-full z-40 mt-2 min-w-[12rem] rounded-md border border-stone-300 bg-white py-1 shadow-lg dark:border-stone-700 dark:bg-[#141414]"
        >
          {group.items.map((item) => {
            const active = pathMatches(pathname, item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                role="menuitem"
                onClick={() => setOpen(false)}
                className={`block px-3 py-2 text-sm ${active ? "bg-stone-100 font-medium text-stone-950 dark:bg-stone-800 dark:text-white" : "text-stone-700 hover:bg-stone-100 hover:text-stone-950 dark:text-stone-300 dark:hover:bg-stone-800 dark:hover:text-white"}`}
              >
                {t(item.key)}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function MobileAccordion({
  group,
  pathname,
  onNavigate,
}: {
  group: NavGroup;
  pathname: string;
  onNavigate: () => void;
}) {
  const t = useTranslations("nav");
  const groupActive = group.items.some((item) =>
    pathMatches(pathname, item.href),
  );
  const [open, setOpen] = useState(groupActive);

  return (
    <div className="border-b border-stone-300 dark:border-stone-800">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={`flex w-full items-center justify-between px-1 py-3 text-left text-sm font-medium ${groupActive ? "text-stone-950 dark:text-white" : "text-stone-700 dark:text-stone-300"}`}
      >
        {t(`groups.${group.key}`)}
        <span aria-hidden className="text-xs opacity-70">
          {open ? "−" : "+"}
        </span>
      </button>
      {open ? (
        <ul className="space-y-1 pb-3 pl-2">
          {group.items.map((item) => (
            <li key={item.href}>
              <NavLink
                item={item}
                pathname={pathname}
                className="block rounded-md px-2 py-2 text-sm"
                onClick={onNavigate}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function NavLink({
  item,
  pathname,
  className = "",
  onClick,
}: {
  item: NavItem;
  pathname: string;
  className?: string;
  onClick?: () => void;
}) {
  const t = useTranslations("nav");
  const active = pathMatches(pathname, item.href);
  return (
    <Link
      href={item.href}
      onClick={onClick}
      className={`${className} ${linkClass(active)} ${active ? "font-medium" : ""}`}
      aria-current={active ? "page" : undefined}
    >
      {t(item.key)}
    </Link>
  );
}

export function SiteNav({ model, extras }: Props) {
  const t = useTranslations("nav");
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const panelId = useId();

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setMobileOpen(false);
    }
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav
        className="hidden items-center gap-x-4 gap-y-2 text-sm lg:flex"
        aria-label={t("main")}
      >
        {model.overview ? (
          <NavLink item={model.overview} pathname={pathname} />
        ) : null}
        {model.flat.map((item) => (
          <NavLink key={item.href} item={item} pathname={pathname} />
        ))}
        {model.groups.map((group) => (
          <DesktopDropdown
            key={group.key}
            group={group}
            pathname={pathname}
          />
        ))}
        {extras}
      </nav>

      <button
        type="button"
        aria-expanded={mobileOpen}
        aria-controls={panelId}
        aria-label={mobileOpen ? t("menuClose") : t("menuOpen")}
        onClick={() => setMobileOpen((value) => !value)}
        className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-stone-400 text-stone-900 lg:hidden dark:border-stone-600 dark:text-stone-100"
      >
        <HamburgerIcon open={mobileOpen} />
      </button>

      {mobileOpen ? (
        <>
          <button
            type="button"
            aria-label={t("menuClose")}
            className="fixed inset-0 top-16 z-40 bg-black/40 lg:hidden sm:top-[4.75rem]"
            onClick={closeMobile}
          />
          <div
            id={panelId}
            className="fixed inset-x-0 top-16 z-50 max-h-[calc(100dvh-4rem)] overflow-y-auto border-b border-stone-300 bg-[#f6f3ee] px-4 py-2 shadow-xl sm:top-[4.75rem] sm:max-h-[calc(100dvh-4.75rem)] lg:hidden dark:border-stone-800 dark:bg-[#0a0a0a]"
          >
            <nav aria-label={t("main")} className="mx-auto max-w-6xl pb-6">
              {model.overview ? (
                <div className="border-b border-stone-300 py-3 dark:border-stone-800">
                  <NavLink
                    item={model.overview}
                    pathname={pathname}
                    className="block text-sm"
                    onClick={closeMobile}
                  />
                </div>
              ) : null}
              {model.flat.map((item) => (
                <div
                  key={item.href}
                  className="border-b border-stone-300 py-3 dark:border-stone-800"
                >
                  <NavLink
                    item={item}
                    pathname={pathname}
                    className="block text-sm"
                    onClick={closeMobile}
                  />
                </div>
              ))}
              {model.groups.map((group) => (
                <MobileAccordion
                  key={group.key}
                  group={group}
                  pathname={pathname}
                  onNavigate={closeMobile}
                />
              ))}

              <div className="mt-4 flex flex-col gap-3 border-t border-stone-300 pt-4 dark:border-stone-800 [&_a]:w-full [&_button]:w-full [&_form]:w-full [&_label]:w-full [&_select]:w-full">
                {extras}
              </div>
            </nav>
          </div>
        </>
      ) : null}
    </>
  );
}
